import { useEffect, useState } from "react";
import { theme, card } from "../theme";
import { api } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";

// My Organisation → Consultant access.
//
// Whether the organisation's consultant may generate policies and policy addenda on its
// behalf. Generation spends the organisation's AI allowance and writes the drafts its
// policy set is built from, so it is the client's decision: only a client administrator
// can change it, and the API (middleware/requireConsultantDocGen.ts) enforces it.

export function ConsultantDocGenPanel() {
  const { user } = useCurrentUser();
  const [state, setState] = useState<{ allowed: boolean; consultancyName: string | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ allowed: boolean; consultancyName: string | null }>("/organizations/consultant-docgen")
      .then(setState)
      .catch(() => {});
  }, []);

  // Nothing to decide for an organisation without a consultancy.
  if (!state || !state.consultancyName) return null;

  const canEdit = user?.role === "client" && !!user.permissions?.includes("org:admin");

  async function toggle(allowed: boolean) {
    setSaving(true);
    setError(null);
    try {
      const r = await api.patch<{ allowed: boolean }>("/organizations/consultant-docgen", { allowed });
      setState((s) => (s ? { ...s, allowed: r.allowed } : s));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Consultant access</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, marginBottom: 14 }}>
        Let {state.consultancyName} generate policies and policy addenda for you. Each generation uses your AI allowance and
        creates a new draft version; drafts only count once you have reviewed and adopted them.
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: theme.color.text, cursor: canEdit ? "pointer" : "default" }}>
        <input
          type="checkbox"
          checked={state.allowed}
          disabled={!canEdit || saving}
          onChange={(e) => void toggle(e.target.checked)}
        />
        Allow my consultant to generate policies and addenda
      </label>
      {!canEdit && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8 }}>
          Only an administrator of this organisation can change this.
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
    </div>
  );
}
